import { useState, useRef, useMemo, useEffect } from 'react';
import {
  Upload,
  Download,
  Building2,
  CheckCircle,
  CheckCircle2,
  XCircle,
  AlertCircle,
  AlertTriangle,
  FileDown,
  Pencil,
  ListFilter,
  Files,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { cutoverApi } from '../../services/cutoverApi';
import type {
  ClinicImportPreviewDto,
  ClinicImportResultDto,
  ClinicImportRowDto,
  ClinicImportRowStatus,
} from '../../types/cutover';
import './ClinicImportTab.css';

type Step = 'upload' | 'preview' | 'result';
type StatusFilter = 'all' | ClinicImportRowStatus;

// v2: row shape gained matched* fields — bumping the key invalidates stale cached previews
const STORAGE_KEY = 'clinic-import-preview-v2';
const PAGE_SIZE = 50;

interface CachedState {
  step: Step;
  preview: ClinicImportPreviewDto | null;
  fileName: string | null;
}

function loadCachedState(): CachedState | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as CachedState) : null;
  } catch {
    return null;
  }
}

function saveCachedState(state: CachedState | null) {
  try {
    if (state) sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    else sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // sessionStorage unavailable — preview just won't survive a refresh
  }
}

/** Extracts the API's { message } body from an axios error, falling back to the generic Error message. */
function errorMessage(err: unknown, fallback: string): string {
  const apiMessage = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
  if (apiMessage) return apiMessage;
  return err instanceof Error ? err.message : fallback;
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  // Attach before clicking and defer revocation — Safari can drop the download
  // for detached anchors or immediately-revoked object URLs.
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/** Escape a CSV field per RFC 4180. */
function csv(value: string | number | null | undefined): string {
  const s = String(value ?? '');
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

const STATUS_LABEL: Record<ClinicImportRowStatus, string> = {
  Ready: 'Ready',
  DuplicateInSystem: 'Duplicate (in system)',
  DuplicateInFile: 'Duplicate (in file)',
  Invalid: 'Invalid',
};

export default function ClinicImportTab() {
  const cached = useMemo(loadCachedState, []);
  const [step, setStep] = useState<Step>(cached?.step === 'preview' ? 'preview' : 'upload');
  const [file, setFile] = useState<File | null>(null);
  const [fileName, setFileName] = useState<string | null>(cached?.fileName ?? null);
  const [preview, setPreview] = useState<ClinicImportPreviewDto | null>(cached?.preview ?? null);
  const [result, setResult] = useState<ClinicImportResultDto | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editRow, setEditRow] = useState<ClinicImportRowDto | null>(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSaveEdit = async () => {
    if (!preview || !editRow) return;
    setSavingEdit(true);
    setError(null);
    try {
      const updated = await cutoverApi.updateClinicImportRow(preview.token, editRow);
      setPreview(updated);
      saveCachedState({ step: 'preview', preview: updated, fileName });
      setEditRow(null);
    } catch (err: unknown) {
      setError(errorMessage(err, 'Failed to save the correction'));
    } finally {
      setSavingEdit(false);
    }
  };

  const setEditField = (key: keyof ClinicImportRowDto, value: string) =>
    setEditRow(prev => (prev ? { ...prev, [key]: value } : prev));

  // Escape dismisses the correction editor (matches the other dialogs in the app)
  useEffect(() => {
    if (!editRow) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !savingEdit) setEditRow(null);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [editRow, savingEdit]);

  const handleDownloadTemplate = async () => {
    setError(null);
    try {
      const blob = await cutoverApi.downloadClinicImportTemplate();
      downloadBlob(blob, 'clinic-import-template.xlsx');
    } catch (err: unknown) {
      setError(errorMessage(err, 'Failed to download the template'));
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] ?? null;
    setFile(f);
    setFileName(f?.name ?? null);
    setError(null);
  };

  const handlePreview = async () => {
    if (!file) return;
    setLoading(true);
    setError(null);
    try {
      const data = await cutoverApi.uploadClinicImport(file);
      setPreview(data);
      setStatusFilter('all');
      setPage(1);
      setStep('preview');
      saveCachedState({ step: 'preview', preview: data, fileName: file.name });
    } catch (err: unknown) {
      setError(errorMessage(err, 'Failed to parse file'));
    } finally {
      setLoading(false);
    }
  };

  const handleExecute = async () => {
    if (!preview) return;
    const confirmed = window.confirm(
      `Create ${preview.readyCount} clinic${preview.readyCount === 1 ? '' : 's'} as Draft? ` +
      `Duplicates and invalid rows are skipped. Draft clinics are activated later through service/pricing verification.`
    );
    if (!confirmed) return;

    setLoading(true);
    setError(null);
    try {
      const data = await cutoverApi.executeClinicImport(preview.token);
      setResult(data);
      setStep('result');
      saveCachedState(null);
    } catch (err: unknown) {
      setError(errorMessage(err, 'Import failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setStep('upload');
    setFile(null);
    setFileName(null);
    setPreview(null);
    setResult(null);
    setError(null);
    setStatusFilter('all');
    saveCachedState(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  /** Export non-Ready rows (with reasons) so problems can be fixed in the source sheet and re-uploaded. */
  const handleExportProblems = () => {
    if (!preview) return;
    const problems = preview.rows.filter(r => r.status !== 'Ready');
    const header = 'Row,Clinic Name,City,State,Zip,Status,Reason';
    const lines = problems.map(r =>
      [r.rowNumber, r.name, r.city, r.state, r.zip, STATUS_LABEL[r.status], r.statusDetail]
        .map(csv).join(','));
    downloadBlob(
      new Blob([[header, ...lines].join('\n')], { type: 'text/csv;charset=utf-8' }),
      'clinic-import-problems.csv');
  };

  const filteredRows = useMemo(() => {
    if (!preview) return [];
    return statusFilter === 'all' ? preview.rows : preview.rows.filter(r => r.status === statusFilter);
  }, [preview, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pagedRows = useMemo(
    () => filteredRows.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
    [filteredRows, currentPage]
  );

  const problemCount = preview ? preview.totalRows - preview.readyCount : 0;

  const statusBadge = (row: ClinicImportRowDto) => (
    <span className={`ci-badge ci-badge-${row.status.toLowerCase()}`} title={row.statusDetail ?? undefined}>
      {STATUS_LABEL[row.status]}
    </span>
  );

  const filterChip = (
    value: StatusFilter,
    label: string,
    count: number,
    icon: React.ReactNode,
    tone: 'all' | 'ready' | 'dup' | 'invalid'
  ) => (
    <button
      type="button"
      className={`ci-chip ci-chip-${tone} ${statusFilter === value ? 'active' : ''}`}
      onClick={() => { setStatusFilter(value); setPage(1); }}
      aria-pressed={statusFilter === value}
    >
      <span className="ci-chip-icon">{icon}</span>
      <span className="ci-chip-meta">
        <span className="ci-chip-count">{count}</span>
        <span className="ci-chip-label">{label}</span>
      </span>
    </button>
  );

  return (
    <div className="ci-tab">
      <div className="ci-header">
        <div className="ci-header-title">
          <span className="ci-header-icon"><Building2 size={20} /></span>
          <h2>Clinic Import</h2>
        </div>
        <p>
          Bulk-create new clinics from a spreadsheet. Imported clinics start as <strong>Draft</strong> —
          services and pricing are verified per clinic afterward.
        </p>
      </div>

      {error && (
        <div className="ci-error">
          <XCircle size={16} />
          <span>{error}</span>
        </div>
      )}

      {step === 'upload' && (
        <div className="ci-upload-section">
          <div className="ci-step-card">
            <span className="ci-step-num">1</span>
            <div className="ci-step-body">
              <h3>Download the template</h3>
              <p>Paste your clinic list into it — the column headers must match exactly.</p>
              <button type="button" className="ci-btn ci-btn-secondary" onClick={handleDownloadTemplate}>
                <Download size={16} />
                Download template (.xlsx)
              </button>
            </div>
          </div>

          <div className="ci-step-card">
            <span className="ci-step-num">2</span>
            <div className="ci-step-body">
              <h3>Upload the filled-in sheet</h3>
              <p>Every row is validated and previewed — nothing is created until you confirm.</p>
              <div className="ci-upload-row">
                <button
                  type="button"
                  className="ci-file-label"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload size={16} />
                  <span>Choose file…</span>
                </button>
                <span className={`ci-filename ${fileName ? '' : 'ci-filename-empty'}`}>
                  {fileName ?? 'No file selected'}
                </span>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx"
                onChange={handleFileChange}
                className="ci-file-input"
              />
              <button
                type="button"
                className="ci-btn ci-btn-primary"
                onClick={handlePreview}
                disabled={!file || loading}
              >
                {loading ? 'Validating…' : 'Preview'}
              </button>
            </div>
          </div>
        </div>
      )}

      {step === 'preview' && preview && (
        <div className="ci-preview-section">
          <div className="ci-chips">
            {filterChip('all', 'All rows', preview.totalRows, <ListFilter size={16} />, 'all')}
            {filterChip('Ready', 'Ready to import', preview.readyCount, <CheckCircle2 size={16} />, 'ready')}
            {filterChip('DuplicateInSystem', 'Already in system',
              preview.rows.filter(r => r.status === 'DuplicateInSystem').length, <AlertTriangle size={16} />, 'dup')}
            {filterChip('DuplicateInFile', 'Duplicated in file',
              preview.rows.filter(r => r.status === 'DuplicateInFile').length, <Files size={16} />, 'dup')}
            {filterChip('Invalid', 'Needs fixing', preview.invalidCount, <XCircle size={16} />, 'invalid')}
            {problemCount > 0 && (
              <button type="button" className="ci-btn ci-btn-secondary ci-export-btn" onClick={handleExportProblems}>
                <FileDown size={14} />
                Export problems
              </button>
            )}
          </div>

          <div className="ci-table-wrapper">
            <table className="ci-table">
              <thead>
                <tr>
                  <th>Row</th>
                  <th>Name</th>
                  <th>Code</th>
                  <th>Address</th>
                  <th>City</th>
                  <th>State</th>
                  <th>Zip</th>
                  <th>Contact</th>
                  <th>Status</th>
                  <th>Detail</th>
                  <th className="ci-actions-col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pagedRows.map((row) => (
                  <tr key={row.rowNumber} className={row.status !== 'Ready' ? 'ci-row-muted' : ''}>
                    <td>{row.rowNumber}</td>
                    <td>{row.name}</td>
                    <td>{row.code ?? '—'}</td>
                    <td>{row.address1}{row.address2 ? `, ${row.address2}` : ''}</td>
                    <td>{row.city}</td>
                    <td>{row.state}</td>
                    <td>{row.zip}</td>
                    <td>{row.contactName ?? '—'}</td>
                    <td>{statusBadge(row)}</td>
                    <td className="ci-detail-cell">
                      {row.matchedClinicName && (
                        <div className="ci-match-info">
                          <span className="ci-match-clinic">
                            {row.matchedClinicName}
                            {row.matchedClinicAddress ? ` — ${row.matchedClinicAddress}` : ''}
                            {row.matchedClinicId ? ` (Id ${row.matchedClinicId})` : ''}
                          </span>
                          <span className="ci-match-chips">
                            {row.matchedFields.map(f => (
                              <span key={`m-${f}`} className="ci-mchip ci-mchip-match" title={`${f} matches the existing clinic`}>✓ {f}</span>
                            ))}
                            {row.mismatchedFields.map(f => (
                              <span key={`d-${f}`} className="ci-mchip ci-mchip-differ" title={`${f} differs from the existing clinic`}>✕ {f}</span>
                            ))}
                          </span>
                        </div>
                      )}
                      {!row.matchedClinicName && (row.statusDetail ?? '')}
                    </td>
                    <td className="ci-edit-cell">
                      <button
                        type="button"
                        className="ci-edit-btn"
                        aria-label={`Edit row ${row.rowNumber}`}
                        title={`Correct this row and re-validate`}
                        onClick={() => setEditRow({ ...row })}
                      >
                        <Pencil size={12} />
                        <span>Edit</span>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredRows.length > 0 && (
            <div className="ci-pagination">
              <span className="ci-pagination-info">
                Showing {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, filteredRows.length)} of {filteredRows.length}
              </span>
              {totalPages > 1 && (
                <span className="ci-pagination-controls">
                  <button
                    type="button"
                    className="ci-btn ci-btn-secondary ci-btn-page"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={currentPage <= 1}
                  >
                    <ChevronLeft size={14} />
                    Prev
                  </button>
                  <span className="ci-pagination-page">Page {currentPage} of {totalPages}</span>
                  <button
                    type="button"
                    className="ci-btn ci-btn-secondary ci-btn-page"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage >= totalPages}
                  >
                    Next
                    <ChevronRight size={14} />
                  </button>
                </span>
              )}
            </div>
          )}

          <div className="ci-actions">
            <button type="button" className="ci-btn ci-btn-secondary" onClick={handleReset}>
              Back
            </button>
            <button
              type="button"
              className="ci-btn ci-btn-primary"
              onClick={handleExecute}
              disabled={loading || preview.readyCount === 0}
            >
              {loading ? 'Importing…' : `Import ${preview.readyCount} Clinic${preview.readyCount === 1 ? '' : 's'}`}
            </button>
          </div>

          {editRow && (
            <div
              className="ci-modal-overlay"
              role="dialog"
              aria-modal="true"
              aria-labelledby="ci-edit-row-title"
              onClick={e => {
                // Click on the dimmed backdrop (not the dialog itself) dismisses
                if (e.target === e.currentTarget && !savingEdit) setEditRow(null);
              }}
            >
              <div className="ci-modal">
                <h3 id="ci-edit-row-title">Correct row {editRow.rowNumber}</h3>
                <p className="ci-modal-hint">
                  Changes are re-validated and duplicate-checked when you save — the row's status updates in place.
                </p>
                <div className="ci-modal-grid">
                  {([
                    ['Clinic Name*', 'name'],
                    ['Clinic Code', 'code'],
                    ['Address 1*', 'address1'],
                    ['Address 2', 'address2'],
                    ['City*', 'city'],
                    ['State*', 'state'],
                    ['Zip*', 'zip'],
                    ['Country', 'country'],
                    ['Phone', 'phone'],
                    ['Fax', 'fax'],
                    ['Email', 'email'],
                    ['Website', 'website'],
                    ['Contact Name', 'contactName'],
                    ['Contact Phone', 'contactPhone'],
                    ['Contact Email', 'contactEmail'],
                  ] as [string, keyof ClinicImportRowDto][]).map(([label, key]) => (
                    <label key={key} className="ci-modal-field">
                      <span>{label}</span>
                      <input
                        type="text"
                        value={(editRow[key] as string | null) ?? ''}
                        onChange={e => setEditField(key, e.target.value)}
                      />
                    </label>
                  ))}
                </div>
                <div className="ci-modal-actions">
                  <button
                    type="button"
                    className="ci-btn ci-btn-secondary"
                    onClick={() => setEditRow(null)}
                    disabled={savingEdit}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="ci-btn ci-btn-primary"
                    onClick={handleSaveEdit}
                    disabled={savingEdit}
                  >
                    {savingEdit ? 'Saving…' : 'Save & re-validate'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {step === 'result' && result && (
        <div className="ci-result-section">
          <div className="ci-result-counts">
            <div className="ci-result-item ci-result-created">
              <CheckCircle size={20} />
              <span><strong>{result.created}</strong> Created (Draft)</span>
            </div>
            <div className="ci-result-item ci-result-skipped">
              <AlertCircle size={20} />
              <span><strong>{result.skipped}</strong> Skipped</span>
            </div>
            <div className="ci-result-item ci-result-failed">
              <XCircle size={20} />
              <span><strong>{result.failed}</strong> Failed</span>
            </div>
          </div>

          {result.created > 0 && (
            <p className="ci-result-note">
              Imported clinics are in Draft status — verify services and pricing per clinic to activate them.
              They'll be picked up automatically by the next geocoding run.
            </p>
          )}

          {result.errors.length > 0 && (
            <div className="ci-errors-list">
              <h3>Errors</h3>
              <ul>
                {result.errors.map((e) => <li key={e}>{e}</li>)}
              </ul>
              <button
                type="button"
                className="ci-btn ci-btn-secondary"
                onClick={() => downloadBlob(
                  new Blob([result.errors.join('\n')], { type: 'text/plain;charset=utf-8' }),
                  'clinic-import-errors.txt')}
              >
                <FileDown size={14} />
                Export errors
              </button>
            </div>
          )}

          <button type="button" className="ci-btn ci-btn-secondary" onClick={handleReset}>
            Import Another File
          </button>
        </div>
      )}
    </div>
  );
}
